import { useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { get, post, del } from '../../api/client';

/** 备注行。键名按线上拼写（camelCase）声明：全局 ResponseInterceptor 已把 snake 转成 camel */
interface OrderNote {
  id: string;
  content: string;
  noteType: string;
  remindAt: string | null;
  createdAt: string;
  /** 仅管理端下发：备注作者姓名，用于审计「这条是谁记的」 */
  userName: string | null;
}

interface OrderBrief {
  id: string;
  orderNumber: string;
  orderCategory: string;
  activityName: string | null;
  serviceType: string | null;
  orderStartDate: string;
  orderEndDate: string | null;
  orderStatus: string;
  approvalStatus: string;
  paymentStatus: string;
  orderAmount: number;
  isDeleted: boolean;
  customerName: string | null;
  userName: string | null;
}

interface OrderNotesView {
  order: OrderBrief;
  notes: OrderNote[];
}

/**
 * 与 web 端 OrderNotes.tsx 的展示集合同源：五个类型、命中不到回落到「备注」。
 * 服务端刻意不做值域枚举（见 dto/order-notes.dto.ts），所以这份清单是展示层常量，
 * 换端只换图标，不要把它当校验依据。
 */
const NOTE_TYPES = [
  { key: '备注', icon: <FileTextOutlined />, color: 'default' },
  { key: '跟进', icon: <UserSwitchOutlined />, color: 'blue' },
  { key: '电话', icon: <PhoneOutlined />, color: 'cyan' },
  { key: '待办', icon: <ThunderboltOutlined />, color: 'gold' },
  { key: '重要', icon: <StarOutlined />, color: 'red' },
] as const;

/** 与后端 NOTE_CONTENT_MAX_LENGTH 同源，超出即 400 */
const NOTE_CONTENT_MAX_LENGTH = 500;

/** 长文折叠阈值，与 web 端一致 */
const LONG_NOTE_CHARS = 80;

const PARAGRAPH_STYLE: CSSProperties = { marginBottom: 0, whiteSpace: 'pre-wrap' };

/**
 * 收起态夹三行，由 index.css 里的这个 class 承担（web 端 `line-clamp-3` 同一实现）。
 * 不写成 React 内联样式：`-webkit-line-clamp` 在首挂载会丢，只有后续 re-render 才补上，
 * 于是第一次进页面的长备注整段摊开。
 */
const CLAMP_CLASS = 'note-clamp-3';

function noteStyle(type: string) {
  return NOTE_TYPES.find((t) => t.key === type) ?? NOTE_TYPES[0];
}

/**
 * 提醒日状态：纯日期串只比字典序，绝不 new Date() 解析——
 * `new Date('2026-09-13')` 按 UTC 零点解释，东八区读出来会差一天。
 */
function remindStatus(remindAt: string | null): 'overdue' | 'today' | 'upcoming' | null {
  if (!remindAt) return null;
  const today = dayjs().format('YYYY-MM-DD');
  if (remindAt < today) return 'overdue';
  if (remindAt === today) return 'today';
  return 'upcoming';
}

const REMIND_SHORTCUTS = [
  { label: '明天', days: 1 },
  { label: '3天后', days: 3 },
  { label: '1周后', days: 7 },
];

function remindTag(remindAt: string) {
  const status = remindStatus(remindAt);
  const color = status === 'overdue' ? 'red' : status === 'today' ? 'orange' : 'blue';
  // 前缀与移动端、契约文档统一为「逾期」，不跟随 web 那句「已逾期」
  const prefix = status === 'overdue' ? '逾期 ' : status === 'today' ? '今日 ' : '';
  return (
    <Tag icon={<ClockCircleOutlined />} color={color}>
      {prefix}
      {remindAt.slice(5)}
    </Tag>
  );
}

export default function OrderNotesPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [input, setInput] = useState('');
  /** 已提交查询的订单号：与输入框分开，避免边打字边发请求 */
  const [orderNumber, setOrderNumber] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [noteType, setNoteType] = useState<string>('备注');
  const [content, setContent] = useState('');
  const [remindAt, setRemindAt] = useState<Dayjs | null>(null);
  /**
   * 展开着的那条长备注。
   *
   * 折叠与否**只看内容长度**，不看 DOM 量出来的溢出（antd `ellipsis.expandable` 要量高度）：
   * 量的结果是「没溢出」时，展开全文这个入口会整枚消失，长文直接铺满屏幕，
   * 而打印窗口、隐藏标签页这类量不到高度的环境每次都命中这条。web 端就是按长度判的，四端保持一致。
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['admin-order-notes', orderNumber],
    queryFn: () => get<OrderNotesView>('/admin/order-notes', { orderNumber }),
    enabled: orderNumber.length > 0,
    retry: false,
  });

  const orderId = data?.order.id;

  const addNote = useMutation({
    mutationFn: (body: { content: string; noteType: string; remindAt: string | null }) =>
      post<OrderNote>(`/admin/order-notes/order/${orderId}`, body),
    onSuccess: () => {
      message.success('备注已添加');
      closeForm();
      void queryClient.invalidateQueries({ queryKey: ['admin-order-notes', orderNumber] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '添加备注失败'),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: string) => del(`/admin/order-notes/order/${orderId}/${noteId}`),
    onSuccess: (_r, noteId) => {
      message.success('备注已删除');
      if (expandedId === noteId) setExpandedId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-order-notes', orderNumber] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除备注失败'),
  });

  const closeForm = () => {
    setFormOpen(false);
    setContent('');
    setNoteType('备注');
    setRemindAt(null);
  };

  const submit = () => {
    const trimmed = content.trim();
    if (!trimmed) {
      message.warning('请填写备注内容');
      return;
    }
    // 提醒日期为空时传 null：@IsOptional() 对 null 与 undefined 都跳过校验，
    // 但传空串会被 @Matches(DAY_INPUT_PATTERN) 挡成 400
    addNote.mutate({ content: trimmed, noteType, remindAt: remindAt ? remindAt.format('YYYY-MM-DD') : null });
  };

  const search = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      message.warning('请输入订单号');
      return;
    }
    setOrderNumber(trimmed);
    setExpandedId(null);
  };

  const order = data?.order;
  const notes = data?.notes ?? [];
  // 与 web / 移动端同一道闸门：已取消的单不给新建与删除入口（服务端也会 403）
  const isCancelled = order?.orderStatus === '已取消';

  return (
    <Card
      title="订单备注"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching} disabled={!order}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!order || isCancelled} onClick={() => setFormOpen(true)}>
            添加备注
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        后台没有订单列表，按订单号定位后可直接查看与补记备注。备注只有「新增 / 删除」，
        没有修改——要改就删了重建。已取消的订单不允许记提醒。
      </Typography.Paragraph>

      <SearchBox value={input} onChange={setInput} onSearch={search} loading={isFetching} />

      {orderNumber && isError ? (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          title={error instanceof Error ? error.message : '查询失败'}
          description="请确认订单号是否完整（形如 ORD-20260911-509582）。"
        />
      ) : null}

      {order ? (
        <>
          <Descriptions
            style={{ marginTop: 16 }}
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            bordered
            items={[
              { key: 'no', label: '订单号', children: order.orderNumber },
              { key: 'act', label: '活动名称', children: order.activityName || order.serviceType || '-' },
              { key: 'cat', label: '分类', children: order.orderCategory },
              {
                key: 'date',
                label: '预约日期',
                children: order.orderEndDate && order.orderEndDate !== order.orderStartDate
                  ? `${order.orderStartDate} ~ ${order.orderEndDate}`
                  : order.orderStartDate,
              },
              { key: 'status', label: '订单状态', children: <Tag>{order.orderStatus}</Tag> },
              { key: 'review', label: '审核状态', children: <Tag>{order.approvalStatus}</Tag> },
              { key: 'pay', label: '付款状态', children: <Tag>{order.paymentStatus}</Tag> },
              { key: 'amount', label: '订单金额', children: `¥${order.orderAmount.toFixed(2)}` },
              { key: 'owner', label: '下单方', children: order.userName || '-' },
              { key: 'customer', label: '客户', children: order.customerName || '-' },
              ...(order.isDeleted
                ? [{ key: 'deleted', label: '档案', children: <Tag color="red">已删除订单</Tag> }]
                : []),
            ]}
          />

          <Typography.Title level={5} style={{ margin: '20px 0 12px' }}>
            备注 / 跟进
            {notes.length > 0 ? (
              <Typography.Text type="secondary" style={{ marginLeft: 8, fontWeight: 400 }}>
                共 {notes.length} 条
              </Typography.Text>
            ) : null}
          </Typography.Title>

          {notes.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无备注，点击「添加备注」记录跟进情况" />
          ) : (
            <Timeline
              items={notes.map((note) => {
                const style = noteStyle(note.noteType);
                const isLong = note.content.length > LONG_NOTE_CHARS;
                const isExpanded = expandedId === note.id;
                return {
                  key: note.id,
                  color: style.color === 'default' ? 'gray' : style.color,
                  content: (
                    <div>
                      <Space size={4} wrap style={{ marginBottom: 4 }}>
                        <Tag color={style.color} icon={style.icon}>
                          {note.noteType}
                        </Tag>
                        {note.remindAt ? remindTag(note.remindAt) : null}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {note.userName || '未知作者'} · {dayjs(note.createdAt).format('MM-DD HH:mm')}
                        </Typography.Text>
                        {!isCancelled ? (
                          <Popconfirm
                            title="删除这条备注？"
                            okText="删除"
                            okButtonProps={{ danger: true }}
                            cancelText="取消"
                            onConfirm={() => deleteNote.mutate(note.id)}
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        ) : null}
                      </Space>
                      <Typography.Paragraph
                        className={isLong && !isExpanded ? CLAMP_CLASS : undefined}
                        style={PARAGRAPH_STYLE}
                      >
                        {note.content}
                      </Typography.Paragraph>
                      {isLong ? (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', fontSize: 12 }}
                          onClick={() => setExpandedId(isExpanded ? null : note.id)}
                        >
                          {isExpanded ? '收起' : '展开全文'}
                        </Button>
                      ) : null}
                    </div>
                  ),
                };
              })}
            />
          )}
        </>
      ) : (
        <Spin spinning={isFetching}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={orderNumber ? '未找到订单' : '输入订单号查看并补记备注'}
            style={{ marginTop: 24 }}
          />
        </Spin>
      )}

      <Modal
        title="添加备注"
        open={formOpen}
        onCancel={closeForm}
        onOk={submit}
        okText="保存"
        cancelText="取消"
        confirmLoading={addNote.isPending}
        okButtonProps={{ disabled: !content.trim() }}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary">类型</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Space size={8} wrap>
                {NOTE_TYPES.map((t) => (
                  <Tag.CheckableTag
                    key={t.key}
                    checked={noteType === t.key}
                    onChange={() => setNoteType(t.key)}
                    icon={t.icon}
                  >
                    {t.key}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </div>
          </div>

          <div>
            <Typography.Text type="secondary">备注内容</Typography.Text>
            <Input.TextArea
              style={{ marginTop: 8 }}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              maxLength={NOTE_CONTENT_MAX_LENGTH}
              showCount
              placeholder="输入备注内容，如跟进情况、电话沟通记录、待办事项..."
            />
          </div>

          <div>
            <Typography.Text type="secondary">提醒日期（选填）</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Space size={8} wrap>
                {REMIND_SHORTCUTS.map((s) => {
                  const day = dayjs().add(s.days, 'day').format('YYYY-MM-DD');
                  return (
                    <Tag.CheckableTag
                      key={s.label}
                      checked={remindAt?.format('YYYY-MM-DD') === day}
                      onChange={(checked) => setRemindAt(checked ? dayjs(day) : null)}
                    >
                      {s.label}
                    </Tag.CheckableTag>
                  );
                })}
              </Space>
            </div>
            <Space style={{ marginTop: 8 }}>
              <DatePicker
                value={remindAt}
                onChange={(v) => setRemindAt(v)}
                allowClear
                placeholder="选择日期"
                disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
              />
              {remindAt ? (
                <Button type="text" icon={<CloseCircleOutlined />} onClick={() => setRemindAt(null)}>
                  清除提醒
                </Button>
              ) : null}
            </Space>
            {remindAt ? (
              <Typography.Paragraph type="warning" style={{ marginTop: 8, marginBottom: 0 }}>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                将在 {remindAt.format('YYYY-MM-DD')} 提醒订单双方跟进此备注
              </Typography.Paragraph>
            ) : null}
          </div>
        </Space>
      </Modal>
    </Card>
  );
}

/** 订单号搜索框：回车与按钮都走同一条提交路径 */
function SearchBox({
  value,
  onChange,
  onSearch,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  onSearch: (v: string) => void;
  loading: boolean;
}) {
  return (
    <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPressEnter={() => onSearch(value)}
        placeholder="输入订单号，如 ORD-20260911-509582"
        allowClear
      />
      <Button type="primary" onClick={() => onSearch(value)} loading={loading}>
        查询
      </Button>
    </Space.Compact>
  );
}
